"use client";

import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import { getChallengeParticipantsWithAuth, getChdeliverPostAuthenticated } from "@/app/funcs/challengeApi";

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "success") return "bg-success";
  if (s === "failed") return "bg-danger";
  if (s === "returned") return "bg-warning text-dark";
  if (s === "review") return "bg-secondary";
  return "bg-light text-dark";
}

function statusLabelAr(status) {
  const s = String(status || "").toLowerCase();
  if (s === "success") return "تم القبول";
  if (s === "failed") return "مرفوض";
  if (s === "returned") return "تمت الإعادة";
  if (s === "review") return "قيد المراجعة";
  if (s === "pending") return "بانتظار المراجعة";
  return status ? String(status) : "—";
}

/**
 * Expandable strip on challenge list cards: members who submitted a chdeliver,
 * with in-app review page + optional wp-admin when the API returns chdeliverEditUrl.
 */
export default function AdminChallengeDeliverablesPreview({ challengeId }) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  /** @type {null | Array<Record<string, unknown>>} */
  const [withDelivery, setWithDelivery] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (withDelivery !== null) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getChallengeParticipantsWithAuth(challengeId);
      const participants = Array.isArray(data?.participants) ? data.participants : [];
      const rows = participants.filter((p) => p?.chdeliverPostId || p?.chdeliver_post_id);
      const enriched = await Promise.all(
        rows.map(async (p) => {
          const deliveryPostId = p.chdeliverPostId || p.chdeliver_post_id;
          try {
            const node = await getChdeliverPostAuthenticated(deliveryPostId);
            const acfStatus = node?.acf?.status;
            return { ...p, deliveryAcfStatus: acfStatus };
          } catch {
            const fallback = p.deliveryStatus || p.delivery_status;
            return { ...p, deliveryAcfStatus: fallback ?? null };
          }
        })
      );
      setWithDelivery(enriched);
    } catch {
      setError("تعذر تحميل المشاركين");
      setWithDelivery([]);
    } finally {
      setLoading(false);
    }
  }, [challengeId, withDelivery]);

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && withDelivery === null) {
      void load();
    }
  };

  const openReview = (e, deliveryPostId) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(`/sections/challenges/${challengeId}/delivery-review/${deliveryPostId}`);
  };

  const openWpAdmin = (e, url) => {
    e.preventDefault();
    e.stopPropagation();
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mt-2 pt-2 border-top border-light" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary w-100 text-start d-flex align-items-center justify-content-between gap-2"
        onClick={toggle}
      >
        <span className="d-flex align-items-center gap-1 min-w-0">
          <i className="bi bi-box-seam flex-shrink-0" aria-hidden />
          <span className="text-truncate">تسليمات الأعضاء</span>
          {withDelivery && withDelivery.length > 0 ? (
            <span className="badge bg-primary flex-shrink-0">{withDelivery.length}</span>
          ) : null}
        </span>
        <i className={`bi bi-chevron-${open ? "up" : "down"} flex-shrink-0`} aria-hidden />
      </button>

      {open && (
        <div className="mt-2 small">
          {loading ? (
            <div className="text-muted py-1">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              جاري التحميل…
            </div>
          ) : null}
          {error ? <div className="text-danger py-1">{error}</div> : null}
          {!loading && withDelivery && withDelivery.length === 0 ? (
            <div className="text-muted py-1">لا توجد تسليمات مرتبطة بالجدول بعد.</div>
          ) : null}
          {!loading && withDelivery && withDelivery.length > 0 ? (
            <ul className="list-unstyled mb-0 text-start">
              {withDelivery.slice(0, 12).map((p) => {
                const name = p.display_name || p.displayName || p.nicename || p.login || "عضو";
                const acfStatus = p.deliveryAcfStatus ?? p.deliveryStatus ?? p.delivery_status;
                const editUrl = p.chdeliverEditUrl || p.chdeliver_edit_url;
                const deliveryPostId = p.chdeliverPostId || p.chdeliver_post_id;
                return (
                  <li
                    key={p.id}
                    className="d-flex align-items-center justify-content-between gap-2 py-1 border-bottom border-light"
                  >
                    <span className="text-dark text-truncate" title={String(name)}>
                      {name}
                    </span>
                    <span className="d-flex align-items-center gap-1 flex-shrink-0">
                      <span
                        className={`badge align-self-center ${statusBadgeClass(acfStatus || "Pending")}`}
                        style={{ fontSize: "0.7rem" }}
                        title={acfStatus ? String(acfStatus) : "Pending"}
                      >
                        {acfStatus ? statusLabelAr(acfStatus) : statusLabelAr("Pending")}
                      </span>
                      {deliveryPostId ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary py-0 px-2 text-nowrap"
                          style={{ fontSize: "0.75rem" }}
                          onClick={(e) => openReview(e, deliveryPostId)}
                        >
                          عرض التسليم
                        </button>
                      ) : null}
                      {editUrl ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-nowrap text-muted"
                          style={{ fontSize: "0.7rem" }}
                          onClick={(e) => openWpAdmin(e, editUrl)}
                        >
                          لوحة التحكم
                        </button>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <div className="mt-2 pt-1">
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                router.push(`/sections/challenges/${challengeId}`);
              }}
            >
              صفحة التحدي والمشاركون ←
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
